/* =========================================================
   AF Wishlist - Core Module
   localStorage cache + Shopify metafield sync for logged-in
   customers. Event-driven, zero dependencies.
   ========================================================= */
(() => {
  'use strict';

  /* SHOP-09: this file is loaded from two places (layout/theme.liquid sitewide,
     and templates/page.wishlist.liquid again for the wishlist page). Both are
     `defer`, so they execute in document order, one fully before the other. Bail
     out of the second execution entirely — not just init() — so the customer
     server sync, click listener and MutationObserver are never doubled. */
  if (window.__afWishlistInited) return;
  window.__afWishlistInited = true;

  const STORAGE_KEY = 'aromatica_wishlist';
  const SYNC_TS_KEY = 'aromatica_wishlist_synced_at';
  const MAX_ITEMS = 100;
  const CURRENCY = '\u09F3';
  const SYNC_URL = '/apps/store/wishlist';
  const COUNTS_URL = '/apps/store/wishlist/counts';

  /* ---- Storage helpers ---- */
  function load() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function save(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) { /* quota exceeded - silent */ }
  }

  function getLastSync() {
    try { return parseInt(localStorage.getItem(SYNC_TS_KEY), 10) || 0; } catch (e) { return 0; }
  }

  function setLastSync(ts) {
    try { localStorage.setItem(SYNC_TS_KEY, String(ts)); } catch (e) { /* silent */ }
  }

  function fire(items) {
    document.dispatchEvent(
      new CustomEvent('wishlist:updated', { detail: { items } })
    );
  }

  /* ---- Sync helpers (App Proxy) ----
     Identity is supplied by the Shopify App Proxy: for a signed-in shopper the app reads
     logged_in_customer_id from the signed params; for a guest the app keys the wishlist by
     anon_id below. The theme sends no customer_id or token either way. window.__customer is
     used only to decide whether to READ the server list (guests read localStorage only) and
     whether to show the guest login banner. */
  const customerId = window.__customer || null;
  const isLoggedIn = !!customerId;

  /* Stable, PII-free per-browser id so guest saves reach the app (activity log + "N saved
     this" counts) and can be merged into the customer on first login. Generated once, kept in
     localStorage; never derived from anything identifying. Sent on every push (guest AND
     logged-in) so the app can claim a guest's prior saves when they sign in. */
  const ANON_KEY = 'aromatica_wl_anon';
  function newAnonId() {
    return (window.crypto && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'a-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  function getAnonId() {
    try {
      let id = localStorage.getItem(ANON_KEY);
      if (!id) {
        id = newAnonId();
        localStorage.setItem(ANON_KEY, id);
      }
      return id;
    } catch (e) {
      // Private mode / storage blocked: still send an id so the push carries one.
      return newAnonId();
    }
  }

  let syncTimer = null;

  function pushToServer(items) {
    // Guests AND logged-in customers push. The App Proxy signs the request; the app attributes
    // it to logged_in_customer_id when present, otherwise to anon_id. Debounced 1s.
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      fetch(SYNC_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items, anon_id: getAnonId() })
      }).catch(() => { /* silent */ });
    }, 1000);
  }

  function pullFromServer() {
    // Only logged-in customers read the server list (cross-device). A guest's anon id is
    // per-browser, the same scope as localStorage, so a guest read adds latency with no gain.
    if (!isLoggedIn) return Promise.resolve(null);

    return fetch(SYNC_URL, { headers: { 'Accept': 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .then(data => data && Array.isArray(data.items) ? data.items : null)
      .catch(() => null);
  }

  /* ---- Public API ---- */
  const Wishlist = {
    add(item) {
      const items = load();
      if (items.some(i => i.handle === item.handle)) return items;

      const entry = {
        handle: item.handle,
        title: item.title || '',
        vendor: item.vendor || '',
        image: item.image || '',
        url: item.url || '/products/' + item.handle,
        price: typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0,
        currency: item.currency || CURRENCY,
        addedAt: Date.now()
      };

      items.push(entry);

      // Cap at MAX_ITEMS - drop oldest
      if (items.length > MAX_ITEMS) {
        items.sort((a, b) => a.addedAt - b.addedAt);
        items.splice(0, items.length - MAX_ITEMS);
      }

      save(items);
      fire(items);
      pushToServer(items);
      return items;
    },

    remove(handle) {
      let items = load();
      items = items.filter(i => i.handle !== handle);
      save(items);
      fire(items);
      pushToServer(items);
      return items;
    },

    has(handle) {
      return load().some(i => i.handle === handle);
    },

    getAll() {
      return load();
    },

    clear() {
      save([]);
      fire([]);
      pushToServer([]);
    },

    count() {
      return load().length;
    }
  };

  window.AromaticaWishlist = Wishlist;

  /* ---- UI: Heart buttons ---- */
  function syncButtons() {
    document.querySelectorAll('.af-wl-btn').forEach(btn => {
      const handle = btn.getAttribute('data-af-wl-handle');
      if (!handle) return;
      const saved = Wishlist.has(handle);
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', String(saved));
      // a11y: a toggle keeps one name (aria-pressed carries the state); the product
      // name tells the hearts in a grid apart
      const title = btn.getAttribute('data-af-wl-title');
      btn.setAttribute('aria-label', title ? 'Save ' + title + ' to wishlist' : 'Save to wishlist');
    });
  }

  function handleBtnClick(e) {
    const btn = e.target.closest('.af-wl-btn');
    if (!btn) return;

    const handle = btn.getAttribute('data-af-wl-handle');
    if (!handle) return;

    e.preventDefault();
    e.stopPropagation();

    if (Wishlist.has(handle)) {
      Wishlist.remove(handle);
    } else {
      const item = {
        handle: handle,
        title: btn.getAttribute('data-af-wl-title') || '',
        vendor: btn.getAttribute('data-af-wl-vendor') || '',
        image: btn.getAttribute('data-af-wl-image') || '',
        url: btn.getAttribute('data-af-wl-url') || '',
        price: btn.getAttribute('data-af-wl-price') || 0
      };
      Wishlist.add(item);
    }
  }

  /* ---- Social proof: "N shoppers saved this" (PDP) ----
     Reads the App-Proxy counts endpoint. Response shape is { counts: {...} }; we read the
     value for this handle defensively (counts[handle] | counts.count | first numeric) and
     only reveal at >= 10 so a sparse number is never shown. */
  function renderSavedCount() {
    const el = document.querySelector('[data-af-wl-saved]');
    if (!el) return;
    const handle = el.getAttribute('data-af-wl-saved');
    if (!handle) return;

    fetch(COUNTS_URL + '?handle=' + encodeURIComponent(handle), { headers: { 'Accept': 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const counts = data.counts;
        let n;
        if (Array.isArray(counts)) {
          // App shape: { counts: [{ handle, saved }] }
          const row = counts.find(c => c && c.handle === handle) || counts[0];
          if (row) n = (typeof row.saved === 'number') ? row.saved : row.count;
        } else if (counts && typeof counts === 'object') {
          // Fallback: { counts: { <handle>: N } } or { counts: { count: N } }
          n = counts[handle];
          if (typeof n !== 'number') n = counts.count;
          if (typeof n !== 'number') {
            const nums = Object.keys(counts).map(k => counts[k]).filter(v => typeof v === 'number');
            if (nums.length) n = nums[0];
          }
        }
        if (typeof n !== 'number') n = 0;
        if (n >= 10) {
          // Build the whole phrase client-side so the server HTML never ships a
          // "0 shoppers saved this" that a non-JS text crawler would read as a weak zero.
          const msgEl = el.querySelector('[data-af-wl-saved-msg]');
          if (msgEl) msgEl.innerHTML = '<b>' + n.toLocaleString('en-IN') + '</b> shoppers saved this';
          const wlText = el.querySelector('[data-af-wl-text]');
          if (wlText) wlText.hidden = false; // reveal only once confirmed >= 10
          // Reveal the row for the wishlist line independently of the sold line: drop the
          // "empty" class (present only when the sold line didn't already show the row).
          el.classList.remove('af-trust__saved--empty');
        }
      })
      .catch(() => { /* silent */ });
  }

  /* ---- UI: Header badge ---- */
  function syncBadge() {
    document.querySelectorAll('.af-wl-badge').forEach(badge => {
      const c = Wishlist.count();
      badge.textContent = c;
      badge.hidden = c === 0;
    });
  }

  /* ---- UI: Guest login banner ---- */
  function renderGuestBanner() {
    const container = document.getElementById('af-wl-page-root');
    if (!container || isLoggedIn) return;

    const loginUrl = window.__loginUrl || '/account/login';
    const banner = document.createElement('div');
    banner.className = 'af-wl-guest-banner';
    banner.innerHTML =
      '<div class="af-wl-guest-banner__inner">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="af-wl-guest-banner__icon">' +
          '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>' +
          '<circle cx="9" cy="7" r="4"/>' +
          '<path d="M23 21v-2a4 4 0 0 0-3-3.87"/>' +
          '<path d="M16 3.13a4 4 0 0 1 0 7.75"/>' +
        '</svg>' +
        '<p class="af-wl-guest-banner__text">' +
          'Log in to save your wishlist across all your devices. ' +
          'Without an account, your wishlist only lives in this browser.' +
        '</p>' +
        '<a href="' + loginUrl + '" class="af-wl-guest-banner__btn">Log In</a>' +
      '</div>';
    container.insertBefore(banner, container.firstChild);
  }

  /* ---- UI: Wishlist page ---- */
  function renderPage() {
    const container = document.getElementById('af-wl-page-root');
    if (!container) return;

    const items = Wishlist.getAll();

    // Check for shared wishlist URL (merged into the list; Wishlist.add skips duplicates)
    const params = new URLSearchParams(window.location.search);
    const sharedHandles = params.get('wl');
    if (sharedHandles) {
      importShared(sharedHandles, container);
      return;
    }

    if (items.length === 0) {
      renderEmpty(container);
      renderGuestBanner();
      return;
    }

    renderGrid(container, items);
    renderGuestBanner();
  }

  function renderEmpty(container) {
    container.innerHTML =
      '<div class="af-wl-empty">' +
        '<div class="af-wl-empty__icon">' +
          '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="icon icon-heart">' +
            '<path stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
          '</svg>' +
        '</div>' +
        '<h2 class="af-wl-empty__heading">Your wishlist is empty</h2>' +
        '<p class="af-wl-empty__text">Save fragrances you want to try and come back to them anytime.</p>' +
        '<a href="/collections/all-fragrances" class="af-wl-empty__cta">Explore Decants</a>' +
      '</div>';
  }

  const CART_ICON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-cart" viewBox="0 0 40 40"><path fill="currentColor" fill-rule="evenodd" d="M20.5 6.5a4.75 4.75 0 0 0-4.75 4.75v.56h-3.16l-.77 11.6a5 5 0 0 0 4.99 5.34h7.38a5 5 0 0 0 4.99-5.33l-.77-11.6h-3.16v-.57A4.75 4.75 0 0 0 20.5 6.5m3.75 5.31v-.56a3.75 3.75 0 1 0-7.5 0v.56zm-7.5 1h7.5v.56a3.75 3.75 0 1 1-7.5 0zm-1 0v.56a4.75 4.75 0 1 0 9.5 0v-.56h2.22l.71 10.67a4 4 0 0 1-3.99 4.27h-7.38a4 4 0 0 1-4-4.27l.72-10.67z"/></svg>';

  function renderGrid(container, items) {
    let html =
      '<div class="af-wl-page__header">' +
        '<h1 class="af-wl-page__title">Your Wishlist</h1>' +
        '<span class="af-wl-page__count">' + items.length + ' item' + (items.length !== 1 ? 's' : '') + '</span>' +
        '<div class="af-wl-page__actions">' +
          '<button type="button" class="af-wl-btn-action" id="af-wl-share">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>' +
            '<span class="af-wl-btn-action__label">Share</span>' +
          '</button>' +
        '</div>' +
      '</div>' +
      '<div class="af-wl-grid">';

    items.forEach(item => {
      html +=
        '<div class="af-wl-card" data-af-wl-card="' + esc(item.handle) + '">' +
          '<div class="af-wl-card__img-wrap">' +
            '<a href="' + esc(safeHref(item.url)) + '">' +
              '<img class="af-wl-card__img" src="' + esc(safeSrc(item.image)) + '" alt="' + esc(item.title) + '" loading="lazy" width="400" height="400">' +
            '</a>' +
            '<button type="button" class="af-wl-card__remove" data-af-wl-remove="' + esc(item.handle) + '" aria-label="Remove ' + esc(item.title) + ' from wishlist">&times;</button>' +
            '<div class="af-wl-card__qa" data-af-wl-qa="' + esc(item.handle) + '"></div>' +
          '</div>' +
          '<div class="af-wl-card__info">' +
            '<h3 class="af-wl-card__title"><a href="' + esc(safeHref(item.url)) + '">' + esc(item.title) + '</a></h3>' +
            '<div class="af-wl-card__vendor">' + esc(item.vendor) + '</div>' +
            '<div class="af-wl-card__price" data-af-wl-price="' + esc(item.handle) + '"></div>' +
          '</div>' +
        '</div>';
    });

    html += '</div>';
    // Container for quick-add modals (outside the grid to avoid layout issues)
    html += '<div id="af-wl-modals"></div>';
    container.innerHTML = html;

    // Bind remove buttons
    container.querySelectorAll('[data-af-wl-remove]').forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.getAttribute('data-af-wl-remove');
        const card = container.querySelector('[data-af-wl-card="' + handle + '"]');
        if (card) {
          card.classList.add('is-removing');
          setTimeout(() => {
            Wishlist.remove(handle);
            renderPage();
          }, 300);
        }
      });
    });

    // Share button
    const shareBtn = document.getElementById('af-wl-share');
    if (shareBtn) shareBtn.addEventListener('click', shareWishlist);

    // Fetch product data - inject quick-add buttons and prices
    fetchProductData(items);
  }

  function formatPrice(price, currency) {
    if (!price && price !== 0) return '';
    // price stored as integer (cents in Shopify) - but BDT uses whole numbers
    // The money_without_currency filter outputs e.g. "853" for BDT
    const num = typeof price === 'number' ? price : parseFloat(price) || 0;
    const taka = Math.round(num / 100);
    return '\u09F3 ' + taka.toLocaleString('en-IN');
  }

  /* ---- Fetch product data - inject quick-add buttons + prices ---- */
  function fetchProductData(items) {
    const fetches = items.map(item =>
      fetch('/products/' + encodeURIComponent(item.handle) + '.js')
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    );

    Promise.all(fetches).then(products => {
      products.forEach((product, i) => {
        if (!product) return;
        const handle = items[i].handle;
        const productId = product.id;
        const available = product.variants.some(v => v.available);

        // --- Price display ---
        const priceEl = document.querySelector('[data-af-wl-price="' + handle + '"]');
        if (priceEl) {
          const minPrice = Math.min(...product.variants.map(v => v.price));
          const maxPrice = Math.max(...product.variants.map(v => v.price));
          if (minPrice === maxPrice) {
            priceEl.textContent = formatPrice(minPrice, CURRENCY);
          } else {
            // N3/SHOP-02: "From <min>" instead of a range, matching price.liquid's card_price branch.
            priceEl.textContent = 'From ' + formatPrice(minPrice, CURRENCY);
          }
        }

        // --- Quick-add button (same as product grid cards) ---
        const qaSlot = document.querySelector('[data-af-wl-qa="' + handle + '"]');
        if (!qaSlot || !available) return;

        const modalId = 'QuickAdd-' + productId + '-afwl';
        const productUrl = '/products/' + encodeURIComponent(handle);

        // Button inside the card image
        qaSlot.innerHTML =
          '<modal-opener data-modal="#' + modalId + '">' +
            '<button type="button" class="quick-add__submit button quick-add__submit--mini af-quickadd__submit" ' +
              'aria-haspopup="dialog" data-product-url="' + esc(productUrl) + '?view=quick-add">' +
              '<span class="visually-hidden">Choose options</span>' +
              '<span class="af-qa__icon svg-wrapper" aria-hidden="true">' + CART_ICON_SVG + '</span>' +
            '</button>' +
          '</modal-opener>';

        // Modal outside the grid
        const modalsContainer = document.getElementById('af-wl-modals');
        if (modalsContainer) {
          const modal = document.createElement('quick-add-modal');
          modal.id = modalId;
          modal.className = 'quick-add-modal af-quickadd';
          modal.innerHTML =
            '<div role="dialog" aria-modal="true" class="quick-add-modal__content" tabindex="-1">' +
              '<button id="ModalClose-' + productId + '" type="button" class="quick-add-modal__toggle">' +
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none" class="icon icon-close"><path d="M.58.52l16.93 16.9M.58 17.42L17.51.52" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              '</button>' +
              '<div id="QuickAddInfo-' + productId + '" class="quick-add-modal__content-info"></div>' +
            '</div>';
          modalsContainer.appendChild(modal);
        }
      });
    });
  }

  /* ---- Share ---- */
  function shareWishlist() {
    const items = Wishlist.getAll();
    if (items.length === 0) return;

    const handles = items.map(i => i.handle).join(',');
    const url = window.location.origin + '/pages/wishlist?wl=' + encodeURIComponent(handles);

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        showToast('Link copied!');
      }).catch(() => {
        fallbackCopy(url);
      });
    } else {
      fallbackCopy(url);
    }
  }

  function fallbackCopy(text) {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      showToast('Link copied!');
    } catch (e) {
      showToast('Copy failed \u2014 check clipboard permissions');
    }
    document.body.removeChild(ta);
  }

  /* ---- Import shared list ---- */
  function importShared(handleStr, container) {
    const handles = Array.from(new Set(handleStr.split(',').filter(Boolean))).slice(0, MAX_ITEMS);
    if (handles.length === 0) {
      renderEmpty(container);
      return;
    }

    container.innerHTML = '<p style="text-align:center;padding:40px;opacity:0.5">Loading shared wishlist...</p>';

    const fetches = handles.map(h =>
      fetch('/products/' + encodeURIComponent(h) + '.js')
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    );

    Promise.all(fetches).then(products => {
      products.forEach(product => {
        if (!product) return;
        Wishlist.add({
          handle: product.handle,
          title: product.title,
          vendor: product.vendor,
          image: product.images && product.images[0] ? product.images[0] : '',
          url: '/products/' + product.handle,
          price: product.price
        });
      });

      // Clean URL
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, '', cleanUrl);

      renderPage();
    });
  }

  /* ---- Toast ---- */
  let toastEl = null;
  let toastTimer = null;

  function showToast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'af-wl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }

    clearTimeout(toastTimer);
    toastEl.textContent = msg;
    // Force reflow for re-animation
    toastEl.classList.remove('is-visible');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');

    toastTimer = setTimeout(() => {
      toastEl.classList.remove('is-visible');
    }, 2000);
  }

  /* ---- Utility ---- */
  // Also escapes quotes: the results go inside attribute values.
  function esc(str) {
    if (!str) return '';
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Saved items live in localStorage / the synced list: only same-site paths for links
  // and http(s) for images, never javascript: or data:.
  function safeHref(url) {
    const u = String(url || '');
    return /^\/(?![\/\\])/.test(u) ? u : '#';
  }

  function safeSrc(url) {
    const u = String(url || '');
    return /^(https:)?\/\//i.test(u) ? u : '';
  }

  /* ---- Init ---- */
  function init() {
    syncButtons();
    syncBadge();
    renderSavedCount();

    // If logged in, reconcile with the server. The server is authoritative - so a removal made
    // on another device propagates here - but we keep local items added since this device's last
    // sync. That does double duty: it merges a guest's items up on first login, and it never drops
    // a just-added item whose debounced push hasn't landed yet.
    if (isLoggedIn) {
      pullFromServer().then(serverItems => {
        if (serverItems) {
          const local = load();
          const lastSync = getLastSync();
          const serverHandles = new Set(serverItems.map(i => i.handle));
          const freshLocal = local.filter(i => !serverHandles.has(i.handle) && (i.addedAt || 0) > lastSync);

          let next = serverItems.concat(freshLocal);
          next.sort((a, b) => (a.addedAt || 0) - (b.addedAt || 0));
          if (next.length > MAX_ITEMS) {
            next = next.slice(next.length - MAX_ITEMS);
          }

          const localHandles = local.map(i => i.handle).sort().join(',');
          const nextHandles = next.map(i => i.handle).sort().join(',');
          if (localHandles !== nextHandles) {
            save(next);
            fire(next);
            syncButtons();
            syncBadge();
          }

          // Only push when this device contributed new local items the server didn't have.
          if (freshLocal.length) {
            pushToServer(next);
          }

          setLastSync(Date.now());
        }

        renderPage();
      });
    } else {
      renderPage();
    }

    // Global click delegation for heart buttons
    document.addEventListener('click', handleBtnClick);

    // React to wishlist changes
    document.addEventListener('wishlist:updated', () => {
      syncButtons();
      syncBadge();
    });

    // Watch for dynamically added buttons (AJAX pagination, quick view)
    let mutTimer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(mutTimer);
      mutTimer = setTimeout(syncButtons, 100);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
